import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * <Canonical /> — keeps `<link rel="canonical">` in sync with the
 * current URL so every route in the sitemap has its own canonical
 * URL, not the homepage.
 *
 * Before this component existed, `public/index.html` hard-coded
 *   <link rel="canonical" href="https://myworld.quest/" />
 * for every SPA route. Ahrefs flagged 91 sitemap URLs as
 * "non-canonical page in sitemap" — Google was told every page
 * canonicalises back to `/`.
 *
 * Now the tag is rewritten on every route change to
 *   https://myworld.quest<pathname>
 * (query strings + hashes intentionally stripped — canonical points
 * at the "clean" URL). Dynamic routes like `/lesson/:code` still
 * get their own canonical because `pathname` includes the code.
 */
const CANONICAL_BASE = "https://myworld.quest";

// Routes that share a canonical (e.g. session_id fragments on /signin)
// or shouldn't be indexed at all (admin, per-child dashboards).
const NO_INDEX_PREFIXES = ["/admin", "/parents/schedule/", "/family", "/link-device"];

export default function Canonical() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Normalise trailing slash for consistency (except for root).
    const path = pathname === "/" ? "/" : pathname.replace(/\/+$/, "");
    const href = `${CANONICAL_BASE}${path}`;
    let tag = document.querySelector('link[rel="canonical"]');
    if (!tag) {
      tag = document.createElement("link");
      tag.setAttribute("rel", "canonical");
      document.head.appendChild(tag);
    }
    tag.setAttribute("href", href);

    // Route-level robots meta — sensitive/dashboard routes get
    // noindex so Google doesn't rank empty per-user pages.
    const shouldNoIndex = NO_INDEX_PREFIXES.some((p) => path.startsWith(p));
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute(
      "content",
      shouldNoIndex ? "noindex, follow" : "index, follow"
    );
  }, [pathname]);

  return null;
}
